import React, { useState, useEffect, useRef, memo } from 'react';
import { loadComicPage } from '../utils/imageDecrypt';
import { useDiscrete } from '../contexts/DiscreteContext';

// Cache for decrypted images to avoid re-decrypting
const imageCache = new Map();

/**
 * Skeleton loader component with shimmer animation
 */
const SkeletonLoader = memo(({ style }) => (
  <div 
    style={{
      ...style,
      position: 'relative',
      overflow: 'hidden',
      backgroundColor: '#2a2a3e',
    }}
  >
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.08) 50%, transparent 100%)',
      animation: 'shimmer 1.5s infinite',
    }} />
    <style>{`
      @keyframes shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
    `}</style>
  </div>
));

/**
 * Component that displays an encrypted image
 * Features: skeleton loader, image caching, progressive loading
 */
export const EncryptedImage = memo(function EncryptedImage({ 
  src, 
  alt, 
  style, 
  className, 
  disableBlur = false,
  priority = false, // Load immediately if true
  ...props 
}) {
  const [imageSrc, setImageSrc] = useState(() => imageCache.get(src) || null);
  const [isLoading, setIsLoading] = useState(!imageCache.has(src));
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);
  
  // Get discrete mode - with fallback if context is not available
  let discreteBlurStyle = {};
  try {
    const discreteContext = useDiscrete();
    if (!disableBlur) {
      discreteBlurStyle = discreteContext.discreteBlurStyle;
    }
  } catch (e) {
    // Context not available, no blur
  }

  useEffect(() => {
    // If already cached, skip
    if (imageCache.has(src)) {
      setImageSrc(imageCache.get(src));
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    
    const loadImage = async () => {
      try {
        const decryptedUrl = await loadComicPage(src);
        if (isMounted) {
          imageCache.set(src, decryptedUrl);
          setImageSrc(decryptedUrl);
        }
      } catch (err) {
        console.warn('Cover load failed:', src, err);
      }

      if (isMounted) {
        setIsLoading(false);
      }
    };
    
    // If priority, load immediately
    if (priority) {
      loadImage();
      return () => { isMounted = false; };
    }
    
    // Use IntersectionObserver for lazy loading
    if ('IntersectionObserver' in window && imgRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loadImage();
            observerRef.current?.disconnect();
          }
        },
        { rootMargin: '100px' } // Start loading 100px before visible
      );
      observerRef.current.observe(imgRef.current);
    } else {
      // Fallback for browsers without IntersectionObserver
      loadImage();
    }
    
    return () => {
      isMounted = false;
      observerRef.current?.disconnect();
    };
  }, [src, priority]);

  const handleImageLoad = () => {
    setIsImageLoaded(true);
  };

  // Show skeleton while loading
  if (isLoading || !imageSrc) {
    return (
      <div ref={imgRef}>
        <SkeletonLoader style={style} />
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', ...style }}>
      {/* Skeleton shown until image fully loads */}
      {!isImageLoaded && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
          <SkeletonLoader style={{ width: '100%', height: '100%' }} />
        </div>
      )}
      <img
        ref={imgRef}
        src={imageSrc}
        alt={alt}
        style={{
          ...style,
          ...discreteBlurStyle,
          opacity: isImageLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease, filter 0.3s ease',
        }}
        className={className}
        onLoad={handleImageLoad}
        {...props}
      />
    </div>
  );
});

export default EncryptedImage;
